import React from 'react';
import classNames from 'classnames';
import { noop } from 'lodash';
import { scaleLinear } from '@visx/scale';
import { LinePath } from '@visx/shape';
import {
  DEFAULT_SERIES_PALETTE,
  applySeriesPalette,
} from './series-radar-utils';
import SeriesRadarLegendFilter from './SeriesRadarLegendFilter';
import styles from './SeriesRadarChart.module.scss';

function SeriesRadarLegend({
  fields,
  heading = 'Series',
  caption = null,
  seriesPalette = DEFAULT_SERIES_PALETTE,
  pinnedKey,
  markerWidth = 26,
  markerHeight = 15,
  onPeek = noop,
  onClearPeek = noop,
  onPin = noop,
  onClearPin = noop,
  enableFilter = false,
  maxSeries = 0,
  selectedKeys = [],
  onChangeSelectedKeys = noop,
}) {
  const showFilter = enableFilter && fields.length > 1;
  const selectedSet = new Set(selectedKeys);
  const visibleFields = applySeriesPalette(
    showFilter ? fields.filter((field) => selectedSet.has(field.key)) : fields,
    seriesPalette
  );

  const handleClick = (key, disabled) => {
    if (disabled) {
      return;
    }
    if (pinnedKey === key) {
      onClearPin();
    } else {
      onPin(key);
    }
  };

  const scaleX = scaleLinear({
    domain: [0, 1],
    range: [0, markerWidth],
  });
  const x = (d) => {
    switch (d) {
      case 'start':
        return 0;
      case 'end':
        return markerWidth;
      default:
        return scaleX(d.value);
    }
  };
  const y = () => markerHeight / 2;
  const markerData = ['start', { value: 0.5, answer: { id: 1 } }, 'end'];
  const markerPoint = markerData[1];

  return (
    <div className={styles.legend}>
      <div className={styles.legendHeading}>
        <label>
          <span>{heading}</span>
          {caption ? (
            <span className={styles.legendBreakoutName}>{` — ${caption}`}</span>
          ) : null}
        </label>
        {showFilter ? (
          <SeriesRadarLegendFilter
            items={fields}
            selectedKeys={selectedKeys}
            maxSeries={maxSeries}
            onChange={onChangeSelectedKeys}
          />
        ) : null}
      </div>
      <div className={styles.legendItems}>
        {visibleFields.map((field) => (
          <span
            className={classNames(styles.legendItem, {
              [styles.legendItemDisabled]: field.disabled,
            })}
            key={field.key}
            role="button"
            tabIndex={field.disabled ? -1 : 0}
            aria-disabled={field.disabled ? true : undefined}
            aria-pressed={field.disabled ? undefined : field.key === pinnedKey}
            title={field.disabled ? 'No responses' : undefined}
            onMouseEnter={() => {
              if (!field.disabled) {
                onPeek(field.key);
              }
            }}
            onMouseLeave={onClearPeek}
            onClick={() => handleClick(field.key, field.disabled)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleClick(field.key, field.disabled);
              }
            }}
          >
            <span className={styles.legendMarker}>
              <svg
                viewBox={`0 0 ${markerWidth} ${markerHeight}`}
                width={markerWidth}
                height={markerHeight}
              >
                <g
                  className={classNames(
                    `palette-${seriesPalette}-${field.paletteIndex}`,
                    field.inkColor && styles.hasInk
                  )}
                  style={
                    field.inkColor
                      ? { '--radar-series-ink': field.inkColor }
                      : undefined
                  }
                >
                  <LinePath
                    className="distribution-curve palette-stroke"
                    data={markerData}
                    x={x}
                    y={y}
                  />
                  <circle
                    className="spacer"
                    cx={x(markerPoint)}
                    cy={y(markerPoint)}
                    r={7}
                  />
                  <circle
                    className="palette-fill"
                    cx={x(markerPoint)}
                    cy={y(markerPoint)}
                    r={5}
                  />
                  <circle
                    className="inner-circle"
                    cx={x(markerPoint)}
                    cy={y(markerPoint)}
                    r={3}
                  />
                </g>
              </svg>
            </span>
            <span className={styles.legendName}>{field.name}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

export default SeriesRadarLegend;
